import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '../../utils/vat';

const SKIP_CONFIRM_KEY = 'verstaani_skip_extra_lookup_confirm';

/**
 * Check if user has opted to skip the confirmation dialog.
 */
export function shouldSkipExtraLookupConfirm() {
  try {
    return localStorage.getItem(SKIP_CONFIRM_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Dialog to confirm over-quota vehicle lookup.
 * Shows different content for trial vs paid users.
 */
function ExtraLookupConfirmDialog({ open, onClose, onConfirm, quotaInfo, isTrial }) {
  const { t } = useTranslation();
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleConfirm = () => {
    if (dontShowAgain) {
      try {
        localStorage.setItem(SKIP_CONFIRM_KEY, 'true');
      } catch {
        // localStorage not available
      }
    }
    onConfirm();
    onClose();
  };

  const used = quotaInfo?.used_this_period || 0;
  const included = quotaInfo?.included_lookups || 0;
  const price = quotaInfo?.extra_lookup_price || '0,30';
  const formattedPrice = typeof price === 'number' ? formatPrice(price) : price;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', pt: 4, pb: 1 }}>
        <Box sx={{ mb: 2 }}>
          <SearchIcon sx={{ fontSize: 48, color: isTrial ? 'info.main' : 'warning.main' }} />
        </Box>
        {t('vehicleLookup.extraTitle')}
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
        {isTrial ? (
          <DialogContentText>
            {t('vehicleLookup.trialMessage')}
          </DialogContentText>
        ) : (
          <>
            <DialogContentText>
              {t('vehicleLookup.extraMessage', { used, included, price: formattedPrice })}
            </DialogContentText>
            <FormControlLabel
              control={
                <Checkbox
                  checked={dontShowAgain}
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                  size="small"
                />
              }
              label={t('vehicleLookup.dontShowAgain')}
              sx={{ mt: 2 }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1, gap: 1, justifyContent: 'center' }}>
        <Button onClick={onClose} variant="outlined">
          {t('Cancel')}
        </Button>
        {!isTrial && (
          <Button onClick={handleConfirm} variant="contained" color="primary">
            {t('vehicleLookup.extraConfirm', { price: formattedPrice })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ExtraLookupConfirmDialog;
